import MomentUtils from '@date-io/moment'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  makeStyles
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import clsx from 'clsx'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'

import QuizRanking from '../../components/QuizRanking'
import { addOrEditQuiz, getQuizReplies } from '../../services/ClubeApi'
import { getLoggedUser, isAuthenticated } from '../../services/auth'

import Context from '../../context'
import history from '../../services/history'

import ImageUploadToS3 from '../../services/ImageUpload'

function QuizForm(props) {
  const { setUser, setAboutCurrentPage, setShowLoadingModal } = useContext(Context)
  const { quizToEdit } = props.location.state || {}

  const questionShape = {
    description: '',
    QuizQuestionAnswers: [
      { answer: '', image: null, correct: 1 },
      { answer: '', image: null, correct: 0 },
      { answer: '', image: null, correct: 0 }
    ]
  }

  const [data, setData] = useState({
    id: quizToEdit?.id,
    title: quizToEdit?.title || '',
    description: quizToEdit?.description || '',
    instructions: quizToEdit?.instructions || '',
    image: quizToEdit?.image || '',
    deadline: quizToEdit?.deadline || moment().add(7, 'days').toISOString(),
    QuizQuestions: quizToEdit?.QuizQuestions || [{ ...questionShape }]
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState(null)
  const [shouldExclude, setShouldExclude] = useState(false)
  const [rankingVisible, setRankingVisible] = useState(false)
  const [quizReplies, setQuizReplies] = useState(null)
  const [loadingQuizReplies, setLoadingQuizReplies] = useState(true)
  const classes = useStyles()

  const setDataIfPossible = param => !loading && setData(data => ({ ...data, ...param }))

  useEffect(() => {
    if (!isAuthenticated) return history.replace('/')

    setUser(getLoggedUser)
    setAboutCurrentPage({
      title: quizToEdit?.title || 'Novo Quiz'
    })
  }, [])

  useEffect(() => {
    if (quizToEdit?.id) {
      if (!quizReplies) {
        getQuizReplies(quizToEdit.id).then(res => {
          setQuizReplies(res)
          setLoadingQuizReplies(false)
        })
      }
    } else {
      setLoadingQuizReplies(false)
    }
  }, [quizToEdit])

  const hideErrorsModal = () => setErrors(null)
  const showExcludeModal = () => setShouldExclude(true)
  const hideExcludeModal = () => setShouldExclude(false)
  const showRanking = () => setRankingVisible(true)
  const hideRanking = () => setRankingVisible(false)

  const addQuestion = () =>
    setDataIfPossible({
      QuizQuestions: [...data.QuizQuestions, { ...questionShape }]
    })

  const excludeQuestion = qIndex => {
    let QuizQuestions = [...data.QuizQuestions].filter((_, index) => index !== qIndex)
    setDataIfPossible({ QuizQuestions })
  }

  const setQuestion = (index, value) => {
    let QuizQuestions = [...data.QuizQuestions]
    QuizQuestions[index].description = value
    setDataIfPossible({ QuizQuestions })
  }

  const excludeAnswer = (qIndex, aIndex) => {
    let QuizQuestions = [...data.QuizQuestions]
    QuizQuestions[qIndex].QuizQuestionAnswers = QuizQuestions[qIndex].QuizQuestionAnswers.filter(
      (_, index) => index !== aIndex
    )
    setDataIfPossible({ QuizQuestions })
  }

  const addAnswer = qIndex => {
    let QuizQuestions = [...data.QuizQuestions]
    QuizQuestions[qIndex] = {
      ...QuizQuestions[qIndex],
      QuizQuestionAnswers: [...QuizQuestions[qIndex].QuizQuestionAnswers, { ...questionShape }]
    }
    setDataIfPossible({ QuizQuestions })
  }

  const setAnswer = (qIndex, aIndex, value) => {
    let QuizQuestions = [...data.QuizQuestions]
    QuizQuestions[qIndex].QuizQuestionAnswers[aIndex].answer = value
    setDataIfPossible({ QuizQuestions })
  }

  const setImage = (qIndex, aIndex, file) => {
    let QuizQuestions = [...data.QuizQuestions]
    QuizQuestions[qIndex].QuizQuestionAnswers[aIndex].image = file
    setDataIfPossible({ QuizQuestions })
  }

  const setCorrect = (qIndex, aIndex, checked) => {
    if (!checked) return

    let QuizQuestions = [...data.QuizQuestions]
    QuizQuestions[qIndex].QuizQuestionAnswers = QuizQuestions[qIndex].QuizQuestionAnswers.map(
      (answer, index) => ({
        ...answer,
        correct: index === aIndex
      })
    )
    setDataIfPossible({ QuizQuestions })
  }

  const checkErrors = () => {
    let errors = []
    // title
    if (!data.title.trim()) errors.push('Sem título')
    // description
    if (!data.description.trim()) errors.push('Sem descrição')
    // instructions
    if (!data.instructions.trim()) errors.push('Sem instruções e regras')
    // image
    if (!data.image) errors.push('Sem imagem de capa')
    else if (
      data.image instanceof File &&
      !['image/jpeg', 'image/png', 'image/gif'].includes(data.image.type)
    )
      errors.push('Tipo de imagem de capa inválido')
    // questions description
    if (data.QuizQuestions.some(({ description }) => !description.trim()))
      errors.push('Alguma pergunta sem enunciado')
    // questions answers
    if (
      data.QuizQuestions.some(({ QuizQuestionAnswers }) =>
        QuizQuestionAnswers.some(({ image, answer }) => !image && !answer)
      )
    )
      errors.push('Alguma resposta sem imagem e sem descrição')

    return !!errors.length && errors
  }

  const save = async () => {
    let errors = checkErrors()
    if (errors) {
      setErrors(errors)
      return
    }

    setLoading(true)
    setShowLoadingModal(true)

    let image = data.image instanceof File ? await ImageUploadToS3(data.image, 'quiz/') : data.image

    let QuizQuestions = await Promise.all(
      data.QuizQuestions.map(async (question, index) => {
        let QuizQuestionAnswer = await Promise.all(
          question.QuizQuestionAnswers.map(async answer => {
            if (!(answer.image instanceof File)) return answer

            let image = await ImageUploadToS3(answer.image, 'quiz/')
            return { ...answer, image }
          })
        )
        return {
          ...question,
          number: index + 1,
          QuizQuestionAnswer
        }
      })
    )

    await addOrEditQuiz({
      ...data,
      image,
      QuizQuestions
    })
    setLoading(false)
    setShowLoadingModal(false)

    history.push('/quiz')
  }

  const exclude = async () => {
    setLoading(true)
    await addOrEditQuiz({
      ...data,
      situation: 2
    })
    setLoading(false)
    history.push('/quiz')
  }

  return (
    <>
      <div className={classes.outerContainer}>
        {loading ? <LinearProgress /> : null}
        <div className={classes.content}>
          <div className={classes.main}>
            <div className={classes.inputGroup}>
              <span>Nome do Quiz</span>
              <input
                value={data.title}
                onChange={e =>
                  setDataIfPossible({
                    title: e.target.value
                  })
                }
              />
            </div>
            <div className={classes.inputGroup}>
              <span>Descrição</span>
              <textarea
                value={data.description}
                onChange={e =>
                  setDataIfPossible({
                    description: e.target.value
                  })
                }
              />
            </div>
            <div className={classes.inputGroup}>
              <span>Instruções e regras</span>
              <textarea
                value={data.instructions}
                onChange={e =>
                  setDataIfPossible({
                    instructions: e.target.value
                  })
                }
              />
            </div>
            <div className={clsx(classes.inputGroup, classes.dateInputGroup)}>
              <span>Data limite</span>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale="pt-br">
                <DateTimePicker
                  format="L [às] LT"
                  ampm={false}
                  value={data.deadline}
                  onChange={mmt =>
                    setDataIfPossible({
                      deadline: mmt.toISOString()
                    })
                  }
                />
              </MuiPickersUtilsProvider>
            </div>
            <div className={classes.divider} />
            <h4 className={classes.subtitle}>Perguntas</h4>
            {data.QuizQuestions.map((question, qIndex, { length: qLength }) => (
              <div key={qIndex} className={classes.group}>
                <div className={clsx(classes.inputGroup, classes.fullRowThreeColumns)}>
                  <span>Pergunta #{qIndex + 1}</span>
                  <input
                    value={question.description}
                    onChange={e => setQuestion(qIndex, e.target.value)}
                    placeholder="Pergunta..."
                  />
                </div>
                {question.QuizQuestionAnswers.map((answer, aIndex, { length: aLength }) => (
                  <React.Fragment key={aIndex}>
                    <h4 className={clsx(classes.subtitle2, classes.fullRowThreeColumns)}>
                      Alternativa {String.fromCharCode(65 + aIndex)}
                      {answer.correct ? (
                        <div className={classes.correctTag}>Alternativa Correta</div>
                      ) : (
                        !quizToEdit &&
                        aLength > 2 && (
                          <div
                            className={classes.excludeAnswer}
                            onClick={() => excludeAnswer(qIndex, aIndex)}
                          >
                            Excluir Alternativa
                          </div>
                        )
                      )}
                    </h4>
                    <div className={classes.inputGroup}>
                      <span>Resposta</span>
                      <input
                        value={answer.answer}
                        onChange={e => setAnswer(qIndex, aIndex, e.target.value)}
                        placeholder="Resposta..."
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <span>Imagem</span>
                      <div>
                        <input
                          className={clsx(
                            answer.image && typeof answer.image === 'string' && classes.pointer
                          )}
                          value={answer.image instanceof File ? answer.image.name : answer.image}
                          placeholder="Selecionar..."
                          readOnly
                          onClick={() =>
                            answer.image &&
                            typeof answer.image === 'string' &&
                            window.open(process.env.REACT_APP_IMAGES_URL + answer.image, '_blank')
                          }
                        />
                        <label>
                          <span className={classes.plainButton2}>Selecionar arquivo</span>
                          <input
                            className={classes.hiddenInput}
                            type="file"
                            accept="image/png,image/jpeg,image/gif"
                            onChange={e => setImage(qIndex, aIndex, e.target.files[0])}
                          />
                        </label>
                      </div>
                    </div>
                    <Checkbox
                      color="primary"
                      classes={{
                        root: classes.checkbox,
                        checked: classes.checkbox
                      }}
                      disableRipple
                      checked={answer.correct}
                      disabled={!!quizToEdit}
                      onChange={e => setCorrect(qIndex, aIndex, e.target.checked)}
                    />
                  </React.Fragment>
                ))}
                <button
                  className={clsx(
                    classes.plainButton,
                    classes.plainButtonGreen,
                    classes.fullRowThreeColumns,
                    classes.end
                  )}
                  type="button"
                  onClick={() => addAnswer(qIndex)}
                >
                  Adicionar alternativa
                </button>
                {!quizToEdit && qLength > 1 && (
                  <>
                    <div className={clsx(classes.divider2, classes.fullRowThreeColumns)} />
                    <button
                      className={clsx(
                        classes.plainButton,
                        classes.plainButtonRed,
                        classes.fullRowThreeColumns,
                        classes.end
                      )}
                      type="button"
                      onClick={() => excludeQuestion(qIndex)}
                    >
                      Excluir pergunta
                    </button>
                  </>
                )}
              </div>
            ))}
            {!quizToEdit && (
              <div className={classes.addButton} onClick={addQuestion}>
                <AddIcon />
                Adicionar pergunta
              </div>
            )}
          </div>
          <div className={classes.aside}>
            <div className={clsx(classes.box, classes.fullRowTwoColumns)}>
              <h4>Imagem de capa</h4>
              <div>
                <label>
                  {data.image ? (
                    <img
                      className={classes.img}
                      src={(image =>
                        image instanceof File
                          ? URL.createObjectURL(image)
                          : process.env.REACT_APP_IMAGES_URL + image)(data.image)}
                    />
                  ) : null}
                  <span className={classes.plainButton}>Selecionar arquivo</span>
                  <input
                    className={classes.hiddenInput}
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    onChange={e =>
                      setDataIfPossible({
                        image: e.target.files[0]
                      })
                    }
                  />
                </label>
                <span className={classes.legend}>Arquivo .png, .jpg ou .gif</span>
              </div>
            </div>
            {quizToEdit?.id && (
              <div className={clsx(classes.box, classes.fullRowTwoColumns)}>
                <h4>Ranking</h4>
                <div className={clsx(!loadingQuizReplies && classes.noPadding)}>
                  {loadingQuizReplies && <LinearProgress />}
                  {!loadingQuizReplies && (
                    <table>
                      <tbody>
                        {quizReplies?.length === 0 && (
                          <tr>
                            <td colSpan={3}>Nenhuma informação</td>
                          </tr>
                        )}
                        {QuizRanking.sortData(quizReplies, quizToEdit?.QuizQuestions?.length)
                          .slice(0, 5)
                          .map(item => (
                            <tr key={item.id}>
                              <td>{item.position}</td>
                              <td>{item.name}</td>
                              <td>
                                {item.correctReplies} - {item.time}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                      {quizReplies.length > 5 && (
                        <tfoot>
                          <tr>
                            <td colSpan="3" onClick={showRanking}>
                              Ver ranking completo
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  )}
                </div>
              </div>
            )}
            {quizToEdit && (
              <button
                className={clsx(classes.plainButton, classes.plainButtonRed)}
                type="button"
                onClick={showExcludeModal}
              >
                Excluir
              </button>
            )}
            <button
              className={clsx(classes.shadowButton, !quizToEdit && classes.fullRowTwoColumns)}
              type="button"
              onClick={save}
            >
              Publicar
            </button>
          </div>
        </div>
      </div>

      {quizToEdit && (
        <QuizRanking
          open={rankingVisible}
          onClose={hideRanking}
          totalQuestions={quizToEdit.QuizQuestions.length}
          data={quizReplies}
        />
      )}

      <Dialog
        open={!!errors}
        onClose={hideErrorsModal}
        transitionDuration={{ enter: 250, exit: 0 }}
      >
        <DialogTitle>Erros encontrados</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Encontramos os seguintes erros:
            <ul>
              {errors?.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            Corrija-os antes de prosseguir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={hideErrorsModal}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shouldExclude} onClose={hideExcludeModal}>
        <DialogTitle>Excluir</DialogTitle>
        <DialogContent>
          <DialogContentText>Deseja excluir o quiz?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={hideExcludeModal}>
            Não
          </Button>
          <Button color="primary" onClick={exclude}>
            Sim
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const useStyles = makeStyles({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Montserrat',
    fontWeight: '500',
    '& label': {
      margin: '0 !important'
    }
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: '45px'
  },
  main: {
    flexGrow: 1,
    display: 'grid',
    flexDirection: 'auto',
    marginRight: 30,
    gap: '30px'
  },
  group: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr) min-content',
    alignItems: 'end',
    padding: '30px',
    gap: '20px 30px',
    backgroundColor: '#ece5fe',
    borderRadius: '4px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    '& > span': {
      fontSize: '16px',
      fontWeight: 700,
      color: '#30287b'
    },
    '& > input, & > div, & textarea': {
      marginTop: '10px',
      background: '#fff',
      border: '2px solid #30287b !important'
    },
    '& input, & textarea': {
      padding: '11px 13px',
      fontSize: '14px',
      color: '#30287b',
      border: 'none'
    },
    '& > div': {
      display: 'flex',
      flexDirection: 'row'
    },
    '& > div input': {
      flexGrow: 1,
      height: '100%'
    },
    '& > input, & > div': {
      height: 44
    },
    '& textarea': {
      minHeight: 132,
      resize: 'none'
    }
  },
  dateInputGroup: {
    width: 'fit-content',
    '& *::before': {
      display: 'none !important'
    },
    '& *::after': {
      display: 'none !important'
    }
  },
  divider: {
    marginTop: '15px',
    borderBottom: '2px solid #b294ff'
  },
  divider2: {
    margin: '10px -30px',
    borderBottom: '2px solid #b294ff'
  },
  subtitle: {
    margin: '15px 0 0',
    fontFamily: 'Matrice',
    fontSize: '18px',
    color: '#30287b'
  },
  subtitle2: {
    height: '25px',
    margin: '10px 0 0',
    fontFamily: 'Matrice',
    fontSize: '16px',
    color: '#30287b',
    lineHeight: '25px'
  },
  aside: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, auto)',
    width: 340,
    gap: '15px'
  },
  fullRowTwoColumns: {
    gridColumn: '1 / 3'
  },
  fullRowThreeColumns: {
    gridColumn: '1 / 4'
  },
  box: {
    border: '2px solid #30287b',
    '& h4': {
      marginBottom: 0,
      fontSize: '14px',
      fontWeight: '700',
      color: '#ffffff',
      lineHeight: '44px',
      textAlign: 'center',
      textTransform: 'uppercase',
      background: '#30287b',
      borderBottom: '2px solid #30287b'
    },
    '& > div': {
      display: 'grid',
      gridTemplateColumns: 'auto',
      gap: '15px',
      padding: '30px'
    },
    '& table': {
      width: '100%',
      '& td': {
        padding: '15px',
        color: '#30287b'
      },
      '& tbody td:first-child': {
        fontSize: '18px',
        fontWeight: 700,
        textAlign: 'center'
      },
      '& tbody td:nth-child(2)': {
        overflow: 'hidden',
        width: '100%',
        fontSize: '14px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      },
      '& tbody td:last-child': {
        fontSize: '14px',
        fontWeight: 500,
        whiteSpace: 'nowrap'
      },
      '& tfoot': {
        fontSize: '12px',
        fontWeight: 700,
        textAlign: 'center',
        textTransform: 'uppercase',
        borderTop: '2px solid #30387b',
        cursor: 'pointer'
      }
    }
  },
  plainButton: {
    display: 'block',
    width: '100%',
    margin: '0',
    padding: '0 20px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#30287b',
    lineHeight: '44px',
    textAlign: 'center',
    textTransform: 'uppercase',
    background: '#ece5fe',
    border: 'none',
    borderRadius: '2px',
    cursor: 'pointer'
  },
  plainButton2: {
    display: 'block',
    margin: '2px',
    padding: '6px 5px',
    fontSize: '11px',
    fontWeight: '700',
    color: '#30287b',
    lineHeight: '24px',
    textAlign: 'center',
    textTransform: 'uppercase',
    background: '#ece5fe',
    border: 'none',
    borderRadius: '2px',
    cursor: 'pointer'
  },
  plainButtonRed: {
    color: '#fff',
    background: '#d73f32'
  },
  plainButtonGreen: {
    color: '#fff',
    background: '#13735e'
  },
  shadowButton: {
    width: '100%',
    fontSize: '12px',
    fontWeight: '700',
    color: '#30287b',
    lineHeight: '44px',
    textAlign: 'center',
    textTransform: 'uppercase',
    background: '#fbbb33',
    border: 'none',
    borderRadius: '2px',
    boxShadow: '4px 4px #4200f5',
    cursor: 'pointer'
  },
  img: {
    width: '100%',
    height: 'auto'
  },
  hiddenInput: {
    visibility: 'hidden',
    opacity: 0,
    position: 'absolute',
    width: 0,
    height: 0,
    margin: 0,
    padding: 0
  },
  legend: {
    fontSize: '12px',
    fontStyle: 'italic',
    color: '#30287b',
    textAlign: 'center'
  },
  ul: {
    listStyle: 'none',
    margin: '15px 0',
    padding: '0',
    '& li': {
      margin: '6px 0 0',
      fontSize: '14px',
      fontStyle: 'italic',
      color: '#30287b'
    }
  },
  correctTag: {
    display: 'inline-block',
    marginLeft: '16px',
    padding: '5px 12px',
    fontFamily: 'Montserrat',
    fontSize: '12px',
    fontWeight: 600,
    color: '#fff',
    lineHeight: '15px',
    backgroundColor: '#13735e',
    borderRadius: 2
  },
  excludeAnswer: {
    display: 'inline-block',
    marginLeft: '16px',
    padding: '5px 12px',
    fontFamily: 'Montserrat',
    fontSize: '12px',
    fontWeight: 600,
    color: '#fff',
    lineHeight: '15px',
    backgroundColor: '#d73f32',
    borderRadius: 2,
    cursor: 'pointer'
  },
  noPadding: {
    padding: '0 !important'
  },
  checkbox: {
    paddingLeft: 0,
    paddingRight: 0,
    color: '#30287b'
  },
  end: {
    justifySelf: 'end',
    width: 'fit-content'
  },
  addButton: {
    justifySelf: 'center',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '11px 15px',
    fontSize: '12px',
    fontWeight: 700,
    color: '#30287b',
    lineHeight: '15px',
    textTransform: 'uppercase',
    border: '2px solid #30287b',
    borderRadius: 4,
    cursor: 'pointer',
    '& svg': {
      marginRight: '6px',
      fontSize: '18px'
    }
  },
  pointer: {
    cursor: 'pointer'
  }
})

export default QuizForm
